import React from "react";
import { TIPOS_ACTIVIDAD } from "../../../constants/tiposActividad";

// Fuente única en src/constants/tiposActividad.js. Alias local para no tocar el
// render ni el fallback existentes (`TIPO_CONFIG[tipo] || TIPO_CONFIG.MAIL`).
const TIPO_CONFIG = TIPOS_ACTIVIDAD;

const formatFecha = (fecha) => {
  if (!fecha) return "";
  const date = new Date(fecha);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
};

const getDateKey = (fecha) => {
  if (!fecha) return "";
  const date = new Date(fecha);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

/**
 * Agrupa actividades por tipo y día, contando cuántas hay de cada combinación
 */
const agruparActividades = (actividades) => {
  const grupos = {};

  actividades.forEach((actividad) => {
    const dateKey = getDateKey(actividad.fecha);
    const key = `${actividad.tipo}-${dateKey}`;

    if (!grupos[key]) {
      grupos[key] = {
        tipo: actividad.tipo,
        fecha: actividad.fecha,
        count: 0,
        actividades: [],
      };
    }
    grupos[key].count++;
    grupos[key].actividades.push(actividad);
  });

  // Ordenar por fecha descendente
  return Object.values(grupos).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
};

/**
 * Componente que muestra las últimas actividades CRM de un cliente como emojis horizontales
 * Agrupa actividades del mismo tipo en el mismo día y muestra "x2", "x3", etc.
 */
const SkeletonPulse = () => (
  <div style={{ display: "flex", gap: 4 }}>
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        style={{
          width: 44,
          height: 36,
          borderRadius: 4,
          backgroundColor: "#d0d0d0",
          animation: "pulse 1.4s ease-in-out infinite",
          opacity: 0.6,
        }}
      />
    ))}
    {/* eslint-disable-next-line quotes */}
    <style>{`@keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }`}</style>
  </div>
);

export const ActividadesRecientes = ({
  actividades = [],
  usuarios = [],
  tipoFiltro = "todos",
  maxItems = 5,
  contactIds = [],
  loading = false,
  ultimaFechaHistorica = null,
}) => {
  if (loading) return <SkeletonPulse />;
  // Construir sets de IDs y emails de los contactos REALES de este cliente para este tipo
  const contactIdSet = new Set(contactIds.filter(Boolean));
  const contactEmails = new Set(
    usuarios.filter((u) => contactIdSet.has(u.id) && u.email).map((u) => u.email.toLowerCase())
  );

  const filtrar = (a) => {
    // SALE activities (ventas cerradas) siempre se muestran en la columna cliente
    if (a.tipo === "SALE" && tipoFiltro === "cliente") return true;
    if (a.tipo === "SALE" && tipoFiltro === "agencia") return false;

    // Si no hay contactos asignados para este tipo, no mostrar nada
    if (contactIdSet.size === 0) return false;

    // Verificar que al menos un destinatario sea contacto REAL de este cliente
    const destinatarios = a.destinatarios || [];
    return destinatarios.some((d) => {
      if (d.usuario_id && contactIdSet.has(d.usuario_id)) return true;
      if (d.email && contactEmails.has(d.email.toLowerCase())) return true;
      return false;
    });
  };

  const actividadesFiltradas = tipoFiltro === "todos" ? actividades : actividades.filter(filtrar);
  const actividadesAgrupadas = agruparActividades(actividadesFiltradas).slice(0, maxItems);

  if (actividadesAgrupadas.length === 0) {
    if (ultimaFechaHistorica) {
      const d = new Date(ultimaFechaHistorica);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = String(d.getFullYear()).slice(-2);
      return (
        <span
          style={{ color: "#999", fontSize: "0.72rem", fontStyle: "italic", display: "block", textAlign: "left" }}
          title={`Última actividad: ${dd}/${mm}/${yy}`}
        >
          Último {dd}/{mm}/{yy}
        </span>
      );
    }
    return (
      <span
        style={{
          color: "#dc3545",
          fontSize: "0.72rem",
          fontStyle: "italic",
          opacity: 0.7,
          display: "block",
          textAlign: "left",
        }}
      >
        Sin contacto
      </span>
    );
  }

  return (
    <div style={{ display: "flex", gap: "4px", justifyContent: "flex-start", flexWrap: "nowrap" }}>
      {actividadesAgrupadas.map((grupo, index) => {
        const config = TIPO_CONFIG[grupo.tipo] || TIPO_CONFIG.MAIL;
        const tooltipText =
          grupo.count > 1
            ? `${grupo.count} ${config.label}s - ${formatFecha(grupo.fecha)}`
            : `${config.label} - ${formatFecha(grupo.fecha)}`;
        const isLast = index === actividadesAgrupadas.length - 1;
        const showSeparator = actividadesAgrupadas.length > 1 && !isLast;

        return (
          <div
            key={`${grupo.tipo}-${index}`}
            style={{ position: "relative", marginRight: showSeparator ? 4 : 0 }}
            title={tooltipText}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: config.bg,
                color: "#fff",
                borderRadius: 4,
                padding: "8px 10px",
                minWidth: 44,
                gap: 4,
              }}
            >
              <span style={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap" }}>
                {config.text}
              </span>
              <span style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1, opacity: 0.9 }}>
                {formatFecha(grupo.fecha)}
              </span>
            </div>
            {grupo.count > 1 && (
              <span
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-8px",
                  backgroundColor: "#333",
                  color: "#fff",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  padding: "2px 5px",
                  borderRadius: "8px",
                  lineHeight: 1,
                }}
              >
                x{grupo.count}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActividadesRecientes;
